import type { PlatformCredentialListResponseV1 } from "@strust/contracts";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import {
  createCredentialEndpoint,
  listCredentialsEndpoint,
  revokeCredentialEndpoint,
} from "./endpoints.ts";

/** The credential list of one operator (its single environment in this installation). */
export const operatorCredentialsKey = (operatorId: string) =>
  ["platform", "credentials", operatorId] as const;

/** Credentials of the operator, secrets masked; needs `platform.credentials.manage`. */
export function useCredentials(operatorId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: operatorCredentialsKey(operatorId),
    enabled,
    queryFn: ({ signal }) =>
      client.call(listCredentialsEndpoint, {
        params: { id: operatorId },
        signal,
      }),
  });
}

/** The credentials of one operator as a table cell reads them (design D2). */
export type OperatorCredentialsState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly list: PlatformCredentialListResponseV1["credentials"] };

/**
 * The credentials of several operators (one query per operator, the same cache as the operator
 * detail), for the Operators tab of an organization (design D2). `enabled` is false without
 * `platform.credentials.manage`: then no request is made and the map is empty.
 */
export function useOperatorsCredentials(
  operatorIds: readonly string[],
  enabled: boolean,
): ReadonlyMap<string, OperatorCredentialsState> {
  const client = useApiClient();
  return useQueries({
    queries: operatorIds.map((id) => ({
      queryKey: operatorCredentialsKey(id),
      enabled,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        client.call(listCredentialsEndpoint, { params: { id }, signal }),
    })),
    combine: (results) =>
      new Map(
        enabled
          ? results.flatMap((result, index) => {
              const id = operatorIds[index];
              if (id === undefined) return [];
              const state: OperatorCredentialsState = result.data
                ? { status: "ready", list: result.data.credentials }
                : result.isError
                  ? { status: "error" }
                  : { status: "loading" };
              return [[id, state] as const];
            })
          : [],
      ),
  });
}

export function useCreateCredential(operatorId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      client.call(createCredentialEndpoint, {
        params: { id: operatorId },
        body: {},
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: operatorCredentialsKey(operatorId) }),
    // The response carries the one-time `client_secret`: it is not kept in the mutation cache.
    gcTime: 0,
  });
}

export function useRevokeCredential(operatorId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { credentialId: string; reason: string }) =>
      client.call(revokeCredentialEndpoint, {
        params: { id: input.credentialId },
        body: { reason: input.reason },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: operatorCredentialsKey(operatorId) }),
  });
}
